import React from 'react';
import {
  Card,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  TextField,
} from '@mui/material';

export interface BehaviouralMeasureRatingCardProps {
  measureName: string;
  value: number;
  onRatingChange: (value: number) => void;
  comment: string;
  onCommentChange: (value: string) => void;
  error?: boolean;
  ratingLabel?: string;
  /** When not set: value === 0 ? 'Reason for N/A *' : 'Comment (optional)' */
  commentLabel?: string;
  /** When false, N/A option is hidden (e.g. self-review behavioural). Default true. */
  allowNa?: boolean;
  /** When true, comment is required for all (e.g. peer behavioural). Default false. */
  commentRequired?: boolean;
  onClearError?: () => void;
}

const LEVEL_LABELS = {
  1: '1 - Development Required',
  2: '2 - Meets Expectation',
  3: '3 - Exceeds Expectation',
} as const;

export function BehaviouralMeasureRatingCard({
  measureName,
  value,
  onRatingChange,
  comment,
  onCommentChange,
  error = false,
  ratingLabel = 'Your rating *',
  commentLabel,
  allowNa = true,
  commentRequired = false,
  onClearError,
}: BehaviouralMeasureRatingCardProps) {
  const effectiveCommentLabel =
    commentLabel ??
    (value === 0 ? 'Reason for N/A *' : commentRequired ? 'Comment *' : 'Comment (optional)');

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {measureName}
      </Typography>

      <FormControl fullWidth margin="dense" size="small" error={error}>
        <InputLabel>{ratingLabel}</InputLabel>
        <Select
          value={value === 0 && !allowNa ? '' : value}
          onChange={(e) => {
            onClearError?.();
            onRatingChange(Number(e.target.value));
          }}
          label={ratingLabel}
        >
          {allowNa && (
            <SelectMenuItem value={0}>
              <em>N/A — exclude from score</em>
            </SelectMenuItem>
          )}
          <SelectMenuItem value={1}>{LEVEL_LABELS[1]}</SelectMenuItem>
          <SelectMenuItem value={2}>{LEVEL_LABELS[2]}</SelectMenuItem>
          <SelectMenuItem value={3}>{LEVEL_LABELS[3]}</SelectMenuItem>
        </Select>
        {error && (
          <FormHelperText>
            {value === 0
              ? 'Please provide a reason in the comment below'
              : 'Please select a rating'}
          </FormHelperText>
        )}
      </FormControl>

      <TextField
        fullWidth
        label={effectiveCommentLabel}
        value={comment}
        onChange={(e) => {
          onClearError?.();
          onCommentChange(e.target.value);
        }}
        margin="dense"
        size="small"
        multiline
        rows={2}
        error={value === 0 && error}
      />
    </Card>
  );
}
